import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Dispatch } from '@reduxjs/toolkit';

import { buildCloudUploadFormData } from 'utils/cloudUpload.utils';

import { Client, RootState, Site, LoggerService, SiteFilter, CommonDocumentService, CommonCloudService, CommonGlobalService, DocumentViewer } from '@umanforge/common';
import type { CloudElementType, UUID } from '@umanforge/common';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';
import { Loading } from '../components/loading.component';
import { Select } from '../components/inputs/select.component';
import { StorageBanner } from '../components/drive/storage_banner.component';
import { DriveExplorer } from '../components/drive/drive_explorer.component';
import { DriveCardAction } from '../components/drive/drive_card_shell.component';
import { BinIcon } from '../components/icons/bin_icon.component';
import { CreateFolderModal } from '../components/drive/create_folder_modal.component';
import { RenameModal } from '../components/drive/rename_modal.component';
import SendFileInput, { FileOrDir } from '@common_components/inputs/sendFileInput.component';
import { BaseModal } from '../components/modals/base_modal.component';
import { Button } from '../components/inputs/button.component';

import { DriveItem } from '../components/drive/drive_item.types';

import { UploadIcon } from 'components/icons/upload.icon';
import { ChevronRightIcon } from 'components/icons/chevron-right.icon';
import { PencilIcon } from 'components/icons/pencil.icon';
import { DownloadIcon } from 'components/icons/download.icon';
import { CloudService } from 'services/cloud.service';
import { ClientService } from 'services/client.service';
import { NavigationService } from 'services/navigation.service';
import { SiteService } from 'services/site.service';
import { ConfirmDeleteModal } from 'components/drive/confirm_delete_modal.component';


interface ClientOption {
  value: string;
  label: string;
}

interface SiteOption {
  value: string;
  label: string;
}

type BuildActionsParams = {
  item: CloudDirFile;
  token: string | null;
  dispatch: Dispatch;
  onRefresh?: ( siteId: UUID | undefined, cloudDirId: UUID | undefined ) => Promise<void> | void;
  onRename: ( item: CloudDirFile ) => void;
  setIsOpenDeleteConfirmPopup: ( isOpen: boolean ) => void;
};

type Stack = {
  id: string;
  name: string;
  items: DriveItem[]
};

export interface CloudDirFile {
  id: string;
  cloudDirFileId?: string;
  dirParentId: string | null;
  siteId: string;
  name: string;
  extension: string | null;
  description: string | null;
  path: string;
  type: CloudElementType;
  isBackup: boolean;
  fileSize: number;
  authorId: string | null;
  lastActivity: string;
  children: CloudDirFile[];
}

const logger: LoggerService = new LoggerService( 'DriveView' );

export function buildActions( t: ( k: string ) => string, { item, token, dispatch, onRename, setIsOpenDeleteConfirmPopup }: BuildActionsParams ): DriveCardAction[] {

  const fileId = item.id;

  return [
    {
      label: t( 'translation:rename' ),
      icon: <PencilIcon className="w-4 h-4 fill-current" />,
      onClick: () => {
        onRename( item );
      },
    },

    {
      label: t( 'common:delete' ),
      icon: <BinIcon className="w-4 h-4 stroke-red-500" />,
      danger: true,
      onClick: () => { setIsOpenDeleteConfirmPopup( true ); },
    },

    {
      label: t( 'translation:download' ),
      icon: <DownloadIcon className="w-4 h-4 fill-current" />,
      onClick: async () => {
        const blob = await CloudService.downloadCloudDirFile(
          fileId,
          token,
          dispatch
        );

        if ( !blob ) {return;}

        const url = window.URL.createObjectURL( blob );
        const a = document.createElement( 'a' );
        a.href = url;
        a.download = item.name + ( item.extension ? item.extension : '' );
        document.body.appendChild( a );
        a.click();
        a.remove();
        window.URL.revokeObjectURL( url );
      },
    },
  ];
}


export function DriveView() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );

  const [ clients, setClients ] = useState<Client[]>( [] );
  const [ selectedClient, setSelectedClient ] = useState<ClientOption | null>( null );
  const [ sites, setSites ] = useState<Site[]>( [] );
  const [ selectedSite, setSelectedSite ] = useState<SiteOption | null>( null );
  const [ searchQuery, setSearchQuery ] = useState<string>( '' );
  const [ searchResults, setSearchResults ] = useState<Array<{ item: CloudDirFile; pathIds: string[]; pathNames: string[]; score: number }>>( [] );
  const [ isLoading, setIsLoading ] = useState( true );
  const [ isCloudLoading, setIsCloudLoading ] = useState( false );
  const loadingRef = useRef( false );

  const [ isCreateFolderOpen, setIsCreateFolderOpen ] = useState( false );
  const [ isCreateFolderLoading, setIsCreateFolderLoading ] = useState( false );
  const [ isUploadModalOpen, setIsUploadModalOpen ] = useState( false );
  const [ isUploadLoading, setIsUploadLoading ] = useState( false );
  const [ uploadFiles, setUploadFiles ] = useState<FileOrDir[]>( [] );
  const [ isRenameOpen, setIsRenameOpen ] = useState( false );
  const [ isRenameLoading, setIsRenameLoading ] = useState( false );
  const [ renameItem, setRenameItem ] = useState<CloudDirFile | null>( null );
  const [ viewerIsLoading, setViewerIsLoading ] = useState<boolean>( false );
  const [ viewerUrl, setViewerUrl ] = useState<string | null>( null );
  const [ viewerName, setViewerName ] = useState<string | null>( null );
  const [ viewerMimeType, setViewerMimeType ] = useState<string | null>( null );

  const [ confirmDeleteFile, setConfirmDeleteFile ] = useState<CloudDirFile | null>( null );

  /* Stockage — valeurs initiales (EN KO), remplies depuis l'API */
  const [ usedKo, setUsedKo ] = useState<number>( 0 );
  const [ totalKo, setTotalKo ] = useState<number>( 0 );
  const [ availableKo, setAvailableKo ] = useState<number | undefined>( undefined );

  /* Navigation dans les dossiers */
  const [ folderStack, setFolderStack ] = useState<Stack[]>( [] );
  const [ cloudTree, setCloudTree ] = useState<CloudDirFile[]>( [] );


  useEffect( () => {
    if ( loadingRef.current ) { return; }
    loadingRef.current = true;

    const checkParams = (): string | undefined => {
      const urlParams = new URLSearchParams( window.location.search );
      const clientId = urlParams.get( 'clientId' );
      const siteId = urlParams.get( 'siteId' );
      const cloudDirFileId = urlParams.get( 'cloudDirFileId' );

      if ( cloudDirFileId != null && cloudDirFileId != '' ) {
        window.history.pushState( {}, document.title, window.location.pathname );
      }

      if ( clientId && siteId && cloudDirFileId ) {
        tryUpdateLocalStorageClient( clientId );
        tryUpdateLocalStorageSite( siteId );
      }
      return cloudDirFileId ?? undefined;
    };

    const load = async () => {
      try {
        const selectedCloudId = checkParams();

        const result = await ClientService.getAllowedClients( null, token, dispatch ) ?? [];
        setClients( result );

        if ( result.length > 0 ) {
          // Restore saved client/site if available
          const savedClientId = ( () => { try { return localStorage.getItem( 'drive:selectedClient' ); } catch ( err ) { logger.warn( 'drive.view: failed to read drive:selectedClient from localStorage', err ); return null; } } )();

          let chosenClient = result[ 0 ];
          if ( savedClientId ) {
            const found = result.find( r => {return r.id === savedClientId;} );
            if ( found ) { chosenClient = found; }
          }

          setSelectedClient( { value: chosenClient.id, label: chosenClient.name } );
          tryUpdateLocalStorageClient( chosenClient.id );

          const loadedSites = await loadSitesForClient( chosenClient.id );
          setSites( loadedSites );

          const savedSiteId = ( () => { try { return localStorage.getItem( 'drive:selectedSite' ); } catch ( err ) { logger.warn( 'drive.view: failed to read drive:selectedSite from localStorage', err ); return null; } } )();

          let chosenSite: Site | null = null;
          if ( savedSiteId ) {
            chosenSite = loadedSites.find( s => {return s.id === savedSiteId;} ) ?? null;
          }
          if ( !chosenSite ) {
            chosenSite = loadedSites[ 0 ];
          }

          if ( chosenSite ) {
            setSelectedSite( { value: chosenSite.id, label: chosenSite.name } );
            await onRefreshCloud( chosenSite.id, selectedCloudId );
          }
        }
      } finally {
        setIsLoading( false );
      }
    };

    load();
  }, [] );

  // update search when query or cloudTree changes
  useEffect( () => {
    performSearch( searchQuery );
  }, [ searchQuery, cloudTree ] );

  // Load cloud info (client/site) to populate storage banner
  useEffect( () => {
    const loadCloudInfo = async () => {
      if ( !selectedClient || !selectedSite ) {
        resetCloudInfos();
        return;
      }

      const info = await CommonCloudService.getClientCloudInfo( selectedClient.value, token, dispatch );
      if ( !info ) {
        resetCloudInfos();
        return;
      }

      const siteInfo = info.sites?.find( site => { return site.id === selectedSite.value; } );
      if ( !siteInfo ) {
        resetCloudInfos();
        return;
      }

      setTotalKo( siteInfo.maxSizeKo );
      setUsedKo( siteInfo.usedSizeKo );
      setAvailableKo(
        siteInfo.maxSizeKo - siteInfo.usedSizeKo
      );
    };

    void loadCloudInfo();
  }, [ selectedClient, selectedSite, cloudTree, token, dispatch ] );


  const clientOptions: ClientOption[] = useMemo( () => {
    return clients.map( ( c: Client ) => { return { value: c.id, label: c.name }; } );
  }, [ clients ] );

  const siteOptions: SiteOption[] = useMemo( () => {
    return sites.map( ( s: Site ) => { return { value: s.id, label: s.name }; } );
  }, [ sites ] );


  const onRefreshCloud = useCallback( async ( siteId: UUID | undefined, cloudDirId?: UUID ) => {
    try {
      if ( siteId ) {
        await loadCloudForSite( siteId, cloudDirId );
      }
    } catch ( err ) {
      logger.warn( 'drive.view: onRefresh action failed', err );
    }
  }, [] );

  const onRenameCloud = useCallback( ( it: CloudDirFile ) => {
    setRenameItem( it );
    setIsRenameOpen( true );
  }, [] );

  const withNavigation = useCallback( ( items: DriveItem[] ): DriveItem[] => {
    return items.map( item => {
      if ( item.type !== 'folder' ) { return item; }
      const folderChildren = item.children ?? [];
      return {
        ...item,
        onClick: () => {
          setFolderStack( prev => {
            return [
              ...prev,
              { id: item.id, name: item.name, items: withNavigation( folderChildren ) }
            ];
          } );
        },
      };
    } );
  }, [] );

  const cloudToDriveItems = ( items: CloudDirFile[] ): DriveItem[] => {
    if ( !Array.isArray( items ) ) { return []; }
    return items.map( item => {
      const children = Array.isArray( item.children ) ? item.children : [];
      const ext = item.extension ? item.extension.replace( /^\./, '' ).toLowerCase() : undefined;
      const canView = CommonDocumentService.isViewableExtension( ext );
      return {
        id: item.id,
        type: item.type === 'directory' ? 'folder' as const : 'file' as const,
        name: item.name,
        size: item.type === 'directory' ? children.length : item.fileSize,
        extension: item.extension ?? undefined,
        lastModified: item.lastActivity ?? undefined,
        children: cloudToDriveItems( children ),
        actions: buildActions( t, {
          item,
          token,
          dispatch,
          onRefresh: ( siteId, parentId ) => { onRefreshCloud( siteId, parentId ); },
          onRename: onRenameCloud,
          setIsOpenDeleteConfirmPopup: () => { setConfirmDeleteFile( item ); }
        } ),
        onClick: item.type !== 'directory' && canView ? () => { void openViewerForItem( item.id, ( item.name + ( item.extension ?? '' ) ) ); } : undefined,
      };
    } );
  };

  const currentItems = useMemo( () => {
    return folderStack.length > 0 ? folderStack[ folderStack.length - 1 ].items : withNavigation( cloudToDriveItems( cloudTree ) );
  }, [ withNavigation, cloudToDriveItems, folderStack, cloudTree ] );

  const currentFolderId = useMemo( () => {
    return folderStack.length > 0 ? folderStack[ folderStack.length - 1 ].id : ( selectedSite?.value ?? null );
  }, [ folderStack, selectedSite ] );


  const displayedItems: DriveItem[] = ( () => {
    if ( searchQuery.trim().length === 0 ) {
      return currentItems;
    }

    const filteredResults = searchResults.filter( r => {return ( r.score ?? 0 ) > 0;} );
    return filteredResults.map( r => {
      const isFolder = r.item.type === 'directory';
      const actions = buildActions( t, {
        item: r.item,
        token,
        dispatch,
        onRefresh: ( siteId, parentId ) => { onRefreshCloud( siteId, parentId ); },
        onRename: onRenameCloud,
        setIsOpenDeleteConfirmPopup: () => { setConfirmDeleteFile( r.item ); }
      } );

      const ext = r.item.extension ? r.item.extension.replace( /^\./, '' ).toLowerCase() : undefined;
      const canView = CommonDocumentService.isViewableExtension( ext );

      const onClick = () => {
        if ( isFolder ) {
          buildStackFromPath( r.pathIds );
        } else if ( canView ) {
          void openViewerForItem( r.item.id, r.item.name );
        }
        // else: non-viewable file — no action
      };

      return {
        id: r.item.id,
        type: isFolder ? 'folder' as const : 'file' as const,
        name: r.item.name,
        size: isFolder ? ( Array.isArray( r.item.children ) ? r.item.children.length : 0 ) : ( r.item.fileSize ?? 0 ),
        extension: r.item.extension ? r.item.extension.replace( /^\./, '' ) : undefined,
        lastModified: r.item.lastActivity ?? undefined,
        actions,
        onClick,
      } as DriveItem;
    } ).sort( ( a, b ) => {
      const ra = filteredResults.find( s => {return s.item.id === a.id;} )?.score ?? 0;
      const rb = filteredResults.find( s => {return s.item.id === b.id;} )?.score ?? 0;
      return rb - ra || a.name.localeCompare( b.name );
    } );
  } )();


  const resetCloudInfos = useCallback( (): void => {
    setTotalKo( 0 );
    setUsedKo( 0 );
    setAvailableKo( undefined );
  }, [] );

  const loadSitesForClient = useCallback( async ( clientId: string ): Promise<Site[]> => {
    const filter: SiteFilter = { filterClients: [ clientId ] };
    return await SiteService.getAllowedSites( filter, token, dispatch ) ?? [];
  }, [ token, dispatch ] );

  const loadCloudForSite = useCallback( async ( siteId: string, cloudDirAndFileId?: string ): Promise<CloudDirFile[]> => {
    setIsLoading( true );
    try {
      const data = await CloudService.getCloudsFromSite( siteId, token, dispatch );
      const clouds: CloudDirFile[] = data ?? [];
      setCloudTree( clouds );

      if ( Array.isArray( clouds ) && clouds.length > 0 ) {
        const firstCloudFolder = clouds[ 0 ];
        const firstCloudFolderChildren = withNavigation( cloudToDriveItems( Array.isArray( firstCloudFolder.children ) ? firstCloudFolder.children : [] ) );

        let selectedCloudId = firstCloudFolder.id;
        let selectedCloudName = firstCloudFolder.name;
        const children: DriveItem[] = firstCloudFolderChildren;

        let stacks = [ { id: selectedCloudId, name: selectedCloudName, items: children } ];

        if ( cloudDirAndFileId ) {
          stacks = getStackForNavOnCloudDirFile( cloudDirAndFileId, firstCloudFolderChildren, stacks );

          if ( stacks.length > 1 ) {
            const parentStack = stacks.find( stack => {
              return stack.items.some( item => { return item.id === cloudDirAndFileId; } );
            } ) ?? stacks[ stacks.length - 1 ];

            selectedCloudId = parentStack.id;
            selectedCloudName = parentStack.name;
          } else {
            stacks = [ { id: selectedCloudId, name: selectedCloudName, items: children } ];
          }
        }

        setFolderStack( stacks );
        tryUpdateLocalStorageSite( siteId );
      } else {
        setFolderStack( [] );
      }
      return clouds;
    } catch {
      setFolderStack( [] );
      return [];
    } finally {
      setIsLoading( false );
    }

  }, [ token, dispatch ] );

  const refreshStorageInfo = useCallback( async () => {
    try {
      if ( !selectedClient || !selectedSite ) { return; }
      const info = await CommonCloudService.getClientCloudInfo(
        selectedClient.value,
        token,
        dispatch
      );

      if ( !info ) { return; }

      const siteInfo = info.sites?.find( site => { return site.id === selectedSite.value; } );
      if ( !siteInfo ) { return; }

      // Keep values in Ko for downstream formatting
      setTotalKo( siteInfo.maxSizeKo );
      setUsedKo( siteInfo.usedSizeKo );
      setAvailableKo( siteInfo.maxSizeKo - siteInfo.usedSizeKo );
    } catch ( err ) {
      logger.warn( 'drive.view: failed to refresh storage info', err );
    }
  }, [ selectedClient, selectedSite, token, dispatch ] );


  const openViewerForItem = async ( id: string, name: string ) => {
    setViewerIsLoading( true );
    try {
      await CloudService.downloadCloudDirFile( id, token, dispatch )
        .then( blob => {
          if ( !blob ) { return; }
          const url = window.URL.createObjectURL( blob );
          if ( viewerUrl ) {
            window.URL.revokeObjectURL( viewerUrl );
          }

          setViewerUrl( url );
          setViewerName( name );
          setViewerMimeType( blob.type ?? null );
        } );
    } catch ( err ) {
      logger.warn( 'drive.view: failed to open viewer for', id, err );
    } finally {
      setViewerIsLoading( false );
    }
  };

  const getStackForNavOnCloudDirFile = ( cloudDirFileId: UUID, cloudDirFiles: DriveItem[], prevStacks: Stack[] = [] ): Stack[] => {
    const findedCloud = cloudDirFiles.find( cloud => {return cloud.id === cloudDirFileId;} );

    let index = 0;
    while ( index < cloudDirFiles.length && !findedCloud ) {
      const cloudDirFile = cloudDirFiles[ index ];
      index += 1;

      // si pas trouvé alors on explore les enfants
      if ( cloudDirFile.children?.length ) {
        const result = getStackForNavOnCloudDirFile(
          cloudDirFileId,
          cloudDirFile.children,
          [
            ...prevStacks,
            {
              id: cloudDirFile.id,
              name: cloudDirFile.name,
              items: withNavigation( cloudDirFile.children ?? [] )
            }
          ]
        );

        if ( result.length ) {
          return result;
        }
      }
    }

    const stack = [];
    if ( findedCloud ) {
      stack.push( ...[
        ...prevStacks,
        {
          id: findedCloud.id,
          name: findedCloud.name,
          items: withNavigation( findedCloud.children ?? [] )
        }
      ] );
    }

    return stack;
  };


  const flattenCloud = ( nodes: CloudDirFile[], parentIds: string[] = [], parentNames: string[] = [] ): Array<{ item: CloudDirFile; pathIds: string[]; pathNames: string[] }> => {
    const out: Array<{ item: CloudDirFile; pathIds: string[]; pathNames: string[] }> = [];
    nodes.forEach( n => {
      const pathIds = [ ...parentIds, n.id ];
      const pathNames = [ ...parentNames, n.name ];
      out.push( { item: n, pathIds, pathNames } );
      if ( Array.isArray( n.children ) && n.children.length > 0 ) {
        out.push( ...flattenCloud( n.children, pathIds, pathNames ) );
      }
    } );
    return out;
  };

  const scoreForMatch = ( name: string, q: string ): number => {
    const n = name.toLowerCase();
    const s = q.toLowerCase();
    if ( n === s ) { return 100; }
    if ( n.startsWith( s ) ) { return 80; }
    if ( n.includes( s ) ) { return 60; }
    // small fuzzy: score by longest common substring length
    let best = 0;
    for ( let i = 0; i < s.length; i++ ) {
      for ( let j = i + 1; j <= s.length; j++ ) {
        const sub = s.substring( i, j );
        const len = sub.length;
        if ( len > best && n.includes( sub ) ) {
          best = len;
        }
      }
    }
    return Math.max( 0, 50 + best );
  };

  const buildStackFromPath = ( ids: string[], nodesParam?: CloudDirFile[] ) => {
    const build = ( idsLocal: string[], nodesLocal: CloudDirFile[] ) => {
      const stack: Stack[] = [];

      let nodes = nodesLocal;
      for ( const id of idsLocal ) {
        const node = nodes.find( n => { return n.id === id; } );
        if ( !node ) { break; }
        const children = Array.isArray( node.children ) ? node.children : [];
        const items = withNavigation( cloudToDriveItems( children ) );
        stack.push( { id: node.id, name: node.name, items } );
        nodes = children;
      }
      return stack;
    };

    const nodesStart = Array.isArray( nodesParam ) ? nodesParam : cloudTree;
    const stack = build( ids, nodesStart );

    if ( stack.length > 0 ) {
      setFolderStack( stack );
    }
  };

  const performSearch = ( q: string ) => {
    if ( !q || q.trim().length === 0 ) { setSearchResults( [] ); return; }
    const flat = flattenCloud( cloudTree );
    const normalizedQ = q.trim().toLowerCase();
    const results = flat
      .map( f => {return  { ...f, score: scoreForMatch( f.item.name, q ) }; } )
      // Only keep items whose name contains the query (case-insensitive)
      .filter( r => {return ( r.item.name ?? '' ).toLowerCase().includes( normalizedQ );} )
      .sort( ( a, b ) => {return b.score - a.score || a.item.name.localeCompare( b.item.name );} );

    setSearchResults( results );
  };

  const tryUpdateLocalStorageSite = ( siteId: string ) => {
    try {
      localStorage.setItem( 'drive:selectedSite', siteId );
    } catch ( err ) {
      logger.warn( 'drive.view: failed to persist selectedSite', err );
    }
  };

  const tryUpdateLocalStorageClient = ( clientId: string ) => {
    try {
      localStorage.setItem( 'drive:selectedClient', clientId );
    } catch ( err ) {
      logger.warn( 'drive.view: failed to persist selectedClient', err );
    }
  };

  const onCreateFolder = async ( name: string ) => {
    if ( !currentFolderId ) { return; }
    setIsCreateFolderLoading( true );
    try {
      await CloudService.postCloudDir( { cloudDirFileId: currentFolderId, name }, token, dispatch );
      if ( selectedSite && selectedSite.value ) {
        await onRefreshCloud( selectedSite.value, currentFolderId );
      }
    } finally {
      setIsCreateFolderLoading( false );
      setIsCreateFolderOpen( false );
    }
  };

  const onUploadFiles = async () => {
    if ( !currentFolderId || uploadFiles.length === 0 ) { return; }

    setIsUploadLoading( true );

    try {
      const formData = await buildCloudUploadFormData( uploadFiles );
      await CloudService.postCloudDirFiles( currentFolderId, formData, token, dispatch );
      try {
        await onRefreshCloud( selectedSite?.value, currentFolderId );
        await refreshStorageInfo();
      } catch ( err ) {
        logger.warn( 'drive.view: failed to refresh cloud after upload file(s)', err );
      }
      setUploadFiles( [] );

      setIsUploadModalOpen( false );
    } catch ( err ) {
      logger.warn( 'drive.view: failed to upload file(s)', err );
    } finally {
      setIsUploadLoading( false );
    }
  };

  const onSiteChange = async ( opt: SiteOption ) => {
    if ( isCloudLoading ) { return; }
    setIsCloudLoading( true );
    setCloudTree( [] );
    setFolderStack( [] );
    setSelectedSite( opt );
    tryUpdateLocalStorageSite( opt.value );
    try {
      await onRefreshCloud( opt.value );
    } finally {
      setIsCloudLoading( false );
    }
  };

  const onClientChange = async ( opt: ClientOption ) => {
    if ( isCloudLoading ) { return; }
    setIsCloudLoading( true );
    setSelectedSite( null );
    setSites( [] );
    setCloudTree( [] );
    setFolderStack( [] );
    try {
      const loadedSites = await loadSitesForClient( opt.value );
      setSites( loadedSites );
      setSelectedClient( opt );
      tryUpdateLocalStorageClient( opt.value );
      if ( loadedSites.length > 0 ) {
        const firstSite = loadedSites[ 0 ];
        setSelectedSite( { value: firstSite.id, label: firstSite.name } );
        await loadCloudForSite( firstSite.id );
      }
    } finally {
      setIsCloudLoading( false );
    }
  };

  const onDeleteSelected = async ( ids: string[] ) => {
    if ( ids.length === 0 ) { return; }
    try {
      for ( const id of ids ) {
        try {
          await CloudService.deleteCloudDirFile( id, token, dispatch );
        } catch ( err ) {
          logger.warn( 'drive.view: failed to delete item', id, err );
        }
      }
    } finally {
      if ( selectedSite && selectedSite.value ) {
        await onRefreshCloud( selectedSite.value, folderStack.pop()?.id );
      }
    }
  };

  const findCloudOnTree = ( cloudDirFileId: UUID | null, cloudDirFiles: CloudDirFile[] ): CloudDirFile | null => {
    let findedCloud = cloudDirFiles.find( cloud => { return cloud.id === cloudDirFileId; } ) ?? null;
    let index = 0;

    while ( index < cloudDirFiles.length && !findedCloud ) {
      const child = cloudDirFiles[ index ];
      index += 1;
      findedCloud = findCloudOnTree( cloudDirFileId, child.children );
    }

    return findedCloud;
  };

  const countFileOnTree = ( cloudDirFileId: UUID | null, cloudDirFile: CloudDirFile | null = null, fileCount: number = 0, folderCount: number = 0 ): { fileCount: number, folderCount: number } => {
    let findedCloudTree: CloudDirFile | null = cloudDirFile;
    if ( !findedCloudTree ) {
      findedCloudTree = findCloudOnTree( cloudDirFileId, cloudTree );

      fileCount += findedCloudTree?.type === 'file' ? 1 : 0;
      folderCount += findedCloudTree?.type === 'directory' ? 1 : 0;
    }

    if ( findedCloudTree ) {
      for ( const child of findedCloudTree.children ) {
        fileCount += child.type === 'file' ? 1 : 0;
        folderCount += child.type === 'directory' ? 1 : 0;
        const result = countFileOnTree( cloudDirFileId, child, fileCount, folderCount );
        fileCount = result.fileCount;
        folderCount = result.folderCount;
      }
    }

    return { fileCount, folderCount };
  };


  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ false } />
      <div className="flex flex-col min-w-[350px] h-full w-full">
        <HomeTopBar title={ t( 'umandrive' ) } needConfirmationToNav={ false } />

        { isLoading
          ? <Loading />
          : (
            <div className="flex flex-col gap-6 p-6 w-full">

              {/* Sélecteurs client + site */}
              <div className="flex gap-4">
                <div className="w-48">
                  <Select<ClientOption>
                    value={ selectedClient ?? undefined }
                    options={ clientOptions }
                    disabled={ clientOptions.length <= 1 }
                    formatOptionLabel={ ( opt: ClientOption ) => { return <span>{ opt.label }</span>; } }
                    onChange={ onClientChange }
                  />
                </div>
                { selectedClient && (
                  <div className="w-48">
                    <Select<SiteOption>
                      key={ selectedClient.value }
                      value={ selectedSite ?? undefined }
                      options={ siteOptions }
                      disabled={ siteOptions.length <= 1 }
                      formatOptionLabel={ ( opt: SiteOption ) => { return <span>{ opt.label }</span>; } }
                      onChange={ onSiteChange }
                    />
                  </div>
                ) }
              </div>

              {/* Bandeau stockage + bouton créer dossier */}
              <div className="flex items-center justify-between gap-4">

                <StorageBanner
                  usedKo={ usedKo }
                  totalKo={ totalKo }
                  availableKo={ availableKo }
                  canCreateFolder={ ( currentFolderId !== null ) && ( ( () => {
                    if ( !me ) { return false; }
                    const siteId = selectedSite?.value ?? null;
                    const clientId = selectedClient?.value ?? null;
                    const isSuper = me.isSuperAdmin === true;
                    const isAdminSite = siteId ? CommonGlobalService.isAdminOfSite( me, siteId ) : false;
                    const isAdminClient = clientId ? CommonGlobalService.isAdminOfClient( me, clientId ) : false;
                    const isSectorAdminForSite = siteId ? CommonGlobalService.getUserSectors( me ).some( s => { return s.siteId === siteId && s.isAdmin; } ) : false;
                    return isSuper || isAdminSite || isAdminClient || isSectorAdminForSite;
                  } )() ) }
                  onCreateFolder={ () => { setIsCreateFolderOpen( true ); } }
                />

                { ( () => {
                  if ( !me ) { return null; }
                  const siteId = selectedSite?.value ?? null;
                  const clientId = selectedClient?.value ?? null;
                  const isSuper = me.isSuperAdmin === true;
                  const isAdminSite = siteId ? CommonGlobalService.isAdminOfSite( me, siteId ) : false;
                  const isAdminClient = clientId ? CommonGlobalService.isAdminOfClient( me, clientId ) : false;
                  const isSectorAdminForSite = siteId ? CommonGlobalService.getUserSectors( me ).some( s => { return s.siteId === siteId && s.isAdmin; } ) : false;
                  const canManage = isSuper || isAdminSite || isAdminClient || isSectorAdminForSite;
                  if ( !canManage ) { return null; }

                  return (
                    <button
                      type="button"
                      onClick={ () => { setIsUploadModalOpen( true ); } }
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-red-500 text-white hover:bg-red-600 transition "
                    >
                      <UploadIcon className="w-4 h-4 fill-current" />
                    </button>
                  );
                } )() }

              </div>

              {/* Fil d'Ariane */}
              { folderStack.length > 0 && (
                <nav className="flex items-center gap-1 text-sm flex-wrap">
                  { folderStack.map( ( entry: Stack, idx: number ) => { return (
                    <span key={ entry.id } className="flex items-center gap-1">
                      <ChevronRightIcon className="w-2 h-3 fill-gray-300" />
                      <button
                        type="button"
                        className={ `transition-colors ${ idx === folderStack.length - 1 ? 'text-gray-800 font-medium' : 'text-gray-500 hover:text-red-500' }` }
                        onClick={ () => { setFolderStack( prev => { return prev.slice( 0, idx + 1 ); } ); } }
                      >
                        { entry.name }
                      </button>
                    </span>
                  ); } ) }
                </nav>
              ) }

              {/* Explorateur grille / liste */}
              <DriveExplorer
                items={ displayedItems }
                searchQuery={ searchQuery }
                onSearchChange={ setSearchQuery }
                onDeleteSelected={ onDeleteSelected }
              />

            </div>
          )
        }
      </div>
      <BaseModal
        title={ t( 'translation:upload_files' ) }
        isOpen={ isUploadModalOpen }
        onClose={ () => {

          if ( isUploadLoading ) { return; }

          setUploadFiles( [] );
          setIsUploadModalOpen( false );

        } }
      >
        { isUploadLoading
          ? <div className="p-4"><Loading /></div>
          : (
            <div className="flex flex-col gap-4 p-4">

              <SendFileInput
                label={ t( 'translation:folder' ) }
                multipleFiles
                acceptFolders={ true }
                onFilesChange={ files => {
                  setUploadFiles( files );
                } }
              />
              <div className="flex justify-end gap-2">
                <Button
                  label={ t( 'translation:cancel' ) }
                  color="#6b7280"
                  colorHover="#4b5563"
                  disable={ isUploadLoading }
                  onClick={ () => {
                    setUploadFiles( [] );
                    setIsUploadModalOpen( false );
                  } }
                />
                <Button
                  label={
                    isUploadLoading
                      ? t( 'translation:uploading' )
                      : t( 'translation:send' )
                  }
                  color="#ef4444"
                  colorHover="#dc2626"
                  disable={ uploadFiles.length === 0 || isUploadLoading }
                  onClick={ onUploadFiles }
                />
              </div>
            </div>
          ) }
      </BaseModal>
      <CreateFolderModal
        isOpen={ isCreateFolderOpen }
        isLoading={ isCreateFolderLoading }
        onClose={ () => { setIsCreateFolderOpen( false ); } }
        onConfirm={ onCreateFolder }
      />
      <RenameModal
        isOpen={ isRenameOpen }
        isLoading={ isRenameLoading }
        initialName={ renameItem?.name ?? '' }
        onClose={ () => { if ( isRenameLoading ) { return; } setIsRenameOpen( false ); setRenameItem( null ); } }
        onConfirm={ async ( name: string ) => {
          if ( !renameItem ) { return; }
          setIsRenameLoading( true );
          try {
            await CloudService.putCloudDirFile( renameItem.id, { name }, token, dispatch );
            if ( selectedSite && selectedSite.value ) {
              await onRefreshCloud( selectedSite.value, renameItem.dirParentId ?? undefined );
            }
          } finally {
            setIsRenameLoading( false );
            setIsRenameOpen( false );
            setRenameItem( null );
          }
        } }
      />
      <DocumentViewer
        url={ viewerUrl }
        originalName={ viewerName }
        mimeType={ viewerMimeType }
        isLoading={ viewerIsLoading }
        onClose={ () => {
          setViewerUrl( null );
          setViewerName( null );
          setViewerMimeType( null );
        } }
      />
      <ConfirmDeleteModal
        isOpen={ confirmDeleteFile != null }
        elementsCount={ countFileOnTree( confirmDeleteFile?.id ?? null ) }
        onClose={ () => { setConfirmDeleteFile( null ); } }
        onConfirm={ async () => {
          if ( confirmDeleteFile && selectedSite ) {
            setIsLoading( true );
            await CloudService.deleteCloudDirFile( confirmDeleteFile.id, token, dispatch );
            await onRefreshCloud( selectedSite.value, confirmDeleteFile.dirParentId ?? undefined );
          }
        } }
      />
    </>
  );
}
